import { z } from "zod";

/** DI-87: the onboarding checks every route records, in display order. */
export const ONBOARDING_CHECKS = [
  "enumeration",
  "source_identity",
  "actual_run",
  "extraction_quality",
  "event_boundaries",
  "duplicates",
  "expected_cost",
  "failure_reporting",
] as const;
export type OnboardingCheck = (typeof ONBOARDING_CHECKS)[number];

export const ONBOARDING_CHECK_LABELS: Record<OnboardingCheck, string> = {
  enumeration: "Enumeration",
  source_identity: "Source identity",
  actual_run: "Actual acquisition or run",
  extraction_quality: "Extraction quality",
  event_boundaries: "Event boundaries",
  duplicates: "Duplicate and update handling",
  expected_cost: "Expected cost",
  failure_reporting: "Failure reporting",
};

export const CHECK_RESULT_LABELS = { passed: "Passed", not_yet: "Not yet", not_applicable: "Not applicable" } as const;

export const QUALIFICATION_LABELS = { experimental: "Experimental", production: "Production" } as const;

export const qualificationSchema = z.strictObject({
  level: z.enum(["experimental", "production"]),
  label: z.string(),
  basis: z.string(),
  assessed_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  checks: z.array(
    z.strictObject({
      check: z.enum(ONBOARDING_CHECKS),
      label: z.string(),
      result: z.enum(["passed", "not_yet", "not_applicable"]),
      evidence: z.string(),
    }),
  ),
  licence: z.strictObject({
    summary: z.string().min(1),
    unknowns: z.array(z.string()),
    restrictions: z.array(z.string()),
  }),
  unsupported: z.array(z.string()),
  historical_limits: z.string().nullable(),
});
export type Qualification = z.infer<typeof qualificationSchema>;

type CheckResult = Qualification["checks"][number]["result"];

/** One check of an onboarding record; `live` marks evidence from a live attempt rather than fixture tests. */
export interface CheckEntry {
  readonly result: CheckResult;
  readonly evidence: string;
  readonly live?: boolean;
}

export interface OnboardingRecord {
  readonly assessedOn: string;
  readonly checks: Readonly<Record<OnboardingCheck, CheckEntry>>;
  readonly licence: Qualification["licence"];
  readonly unsupported: readonly string[];
  readonly historicalLimits: string | null;
}

/**
 * DI-87: production only with demonstrated coverage. A route qualifies for production when the actual
 * acquisition or run passed in a live attempt and no applicable check is still open; otherwise it is experimental.
 */
export function qualify(record: OnboardingRecord): Qualification {
  const open = ONBOARDING_CHECKS.filter((c) => record.checks[c].result === "not_yet");
  const liveRun = record.checks.actual_run.result === "passed" && record.checks.actual_run.live === true;
  const level = liveRun && open.length === 0 ? "production" : "experimental";
  const basis =
    level === "production"
      ? "Every applicable check passed, with a live run"
      : [
          liveRun ? null : "No live run has been demonstrated",
          open.length > 0 ? `Not yet: ${open.map((c) => ONBOARDING_CHECK_LABELS[c]).join(", ")}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
  return {
    level,
    label: QUALIFICATION_LABELS[level],
    basis,
    assessed_on: record.assessedOn,
    checks: ONBOARDING_CHECKS.map((check) => ({
      check,
      label: ONBOARDING_CHECK_LABELS[check],
      result: record.checks[check].result,
      evidence: record.checks[check].evidence,
    })),
    licence: record.licence,
    unsupported: [...record.unsupported],
    historical_limits: record.historicalLimits,
  };
}
